
<script setup lang="ts">

import { CDN } from '@/models/cdn.ts'
import StyledImage from '@/components/Elements/Images/StyledImage.vue'
import GenericIconWithColoredNumber from '@/components/Elements/Images/Icons/GenericIconWithColoredNumber.vue'
import StandardCard from '@/components/Elements/Cards/StandardCard.vue'
import MetricDisplay from '@/components/Elements/Generic/MetricDisplay.vue'
import GenericIconWithNumber from '@/components/Elements/Images/Icons/GenericIconWithNumber.vue'
import { useAssetStore } from '@/stores/assetStore.ts'
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'

const { t } = useI18n()

/**
 * Stores / models
 */
const assetStore = useAssetStore()

/**
 * Props
 */
const props = defineProps({
  buildingCode: {
    type: String,
    required: true
  }
});

/**
 * Computed
 */
const buildingData = computed(() => assetStore.findBuildingByCode(props.buildingCode)!)

const productionObject = computed(() => assetStore.findProjectObjectByBuildingCode(props.buildingCode)!)

const output = computed(() => {
  return buildingData.value.effects.filter((effect: { bonus: number; type: string }) => effect.bonus > 0);
});

const operationCost = computed(() => {
  return buildingData.value.effects.filter((effect: { bonus: number; type: string }) => effect.bonus < 0);
});



const title = computed(() => {
  return '<div class="flex justify-between"><div>' + t(`buildings.buildings.${buildingData.value.buildingCode}.name`) + '</div><div class="pr-2 flex "><img src="' + CDN.icon('planet') + '" ></div></div>'
})


</script>

<template>
  <StandardCard :title="title" top-padding="0" bottom-padding="2">
    <div class="flex">
      <StyledImage :src="CDN.building(128, buildingData.buildingCode)" size="md" class="shrink-0" />
      <div class="ps-2 w-full  flex flex-col justify-between">
        <div>
          {{ t(`buildings.buildings.${buildingData.buildingCode}.description`) }}
          <span v-if="productionObject.limitations === 'no_ringworld'" class="block text-red-500 mt-1">
            {{ t('modals.building.cannotBuildOnRingworlds') }}
          </span>
          <span v-if="productionObject.limitations === 'only_ringworld'" class="block text-red-500 mt-1">
            {{ t('modals.building.onlyOnRingworlds') }}
          </span>
        </div>

        <div>
          <div class="justify-start hidden lg:flex 2xl:hidden">
            <div class="pt-2">
              <MetricDisplay v-if="operationCost.length>0" :text="t('modals.building.operatingCost')">
                <template v-for="effect in operationCost" :key="`${effect.type}-${effect.bonus}`">
                  <GenericIconWithColoredNumber :amount="effect.bonus" :type="effect.type"/>
                </template>
              </MetricDisplay>
            </div>
          </div>
          <div class="flex justify-between flex-col lg:flex-row">
            <div class="pt-2">
              <MetricDisplay :text="t('modals.building.construction')"><GenericIconWithNumber :amount="productionObject.productionCost" type="production"/></MetricDisplay>
            </div>
            <div class="pt-2  block lg:hidden 2xl:block">
              <MetricDisplay v-if="operationCost.length>0" :text="t('modals.building.operatingCost')">
                <template v-for="effect in operationCost" :key="`${effect.type}-${effect.bonus}`">
                  <GenericIconWithColoredNumber :amount="effect.bonus" :type="effect.type"/>
                </template>
              </MetricDisplay>
            </div>
            <div class="pt-2">
              <MetricDisplay v-if="output.length>0" :text="t('modals.building.output')">
                <template v-for="effect in output" :key="`${effect.type}-${effect.bonus}`">
                  <GenericIconWithColoredNumber :amount="effect.bonus" :type="effect.type"/>
                </template>
              </MetricDisplay>
            </div>
          </div>
        </div>
      </div>
    </div>
  </StandardCard>
</template>

<style scoped lang="scss">

</style>